import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Check, Flame, ArrowRight, ArrowLeft, FileDown, Loader2, CheckCircle, Eye, Save } from "lucide-react";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import AppHeader from "@/components/AppHeader";
import { saveDraft, loadDraft, deleteDraft } from "@/utils/offlineStorage";
import BuyCreditsModal from "@/components/BuyCreditsModal";
import { useCreditPrompt } from "@/hooks/useCreditPrompt";
import PdfPreviewModal from "@/components/PdfPreviewModal";
import SignaturePad from "@/components/SignaturePad";
import { authFetch } from "@/utils/authFetch";

const API = process.env.REACT_APP_BACKEND_URL;

const Label = ({ children }) => (
  <label className="block text-xs uppercase tracking-[0.15em] font-bold mb-1.5" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-secondary)' }}>{children}</label>
);

const YesNo = ({ value, onChange, label }) => (
  <div className="flex items-center gap-4">
    <span className="text-xs font-bold" style={{ color: 'var(--text-secondary)' }}>{label}</span>
    <label className="flex items-center gap-1.5 cursor-pointer">
      <input type="radio" name={label} checked={value === "yes"} onChange={() => onChange("yes")} className="accent-[#EF4444]" />
      <span className="text-xs">Yes</span>
    </label>
    <label className="flex items-center gap-1.5 cursor-pointer">
      <input type="radio" name={label} checked={value === "no"} onChange={() => onChange("no")} className="accent-[#EF4444]" />
      <span className="text-xs">No</span>
    </label>
  </div>
);

const STEPS = [
  { num: 1, label: "Details & Controls" },
  { num: 2, label: "System & Readings" },
  { num: 3, label: "Review & Generate" },
];

export default function GasSafeWizard() {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [draftSaved, setDraftSaved] = useState(false);
  const [signature, setSignature] = useState("");
  const { showModal, modalCreditType, promptBuyCredits, closeModal, buyCredits } = useCreditPrompt();
  const draftKey = "draft_gas_boiler_checklist";

  const [form, setForm] = useState({
    // Customer
    customer_name: "", customer_phone: "", customer_address: "",
    boiler_make_model: "", boiler_serial: "",
    // Engineer
    engineer_name: user?.name || "", gas_safe_no: "",
    company_name: user?.company || "", company_address: "", company_phone: "",
    commissioning_date: null,
    building_reg_number: "",
    // Controls
    ctrl_room_thermostat: false, ctrl_prog_room_thermostat: false,
    ctrl_load_weather_comp: false, ctrl_optimum_start: false,
    ctrl_cyl_thermostat: false, ctrl_combi_boiler: false,
    ctrl_heating_zone_fitted: false, ctrl_heating_zone_not_req: false,
    ctrl_hw_zone_fitted: false, ctrl_hw_zone_not_req: false,
    ctrl_trv_fitted: false, ctrl_trv_not_req: false,
    ctrl_bypass_fitted: false, ctrl_bypass_not_req: false,
    ctrl_boiler_interlock: false,
    // All Systems
    system_flushed: "", system_cleaner: "", system_inhibitor: "",
    primary_filter: "", filter_quantity: "",
    // Central Heating Mode
    ch_gas_rate: "", ch_gas_rate_unit: "m³/hr",
    ch_burner_pressure: "", ch_gas_inlet_pressure: "",
    ch_flow_temp: "", ch_return_temp: "",
    // Combi Only
    is_hard_water: "", scale_reducer_fitted: "", scale_reducer_type: "",
    // DHW Mode
    dhw_gas_rate: "", dhw_gas_rate_unit: "m³/hr",
    dhw_burner_pressure: "", dhw_gas_inlet_pressure: "",
    dhw_cold_inlet_temp: "", dhw_outlets_checked: "",
    dhw_flow_rate: "", dhw_flow_temp: "",
    // Condensing
    condensate_installed: "",
    // All Installations
    co_max_ppm: "", co_max_ratio: "",
    co_min_ppm: "", co_min_ratio: "",
    complies_building_regs: "", installed_per_instructions: "",
    demonstrated_to_customer: "", literature_left: "",
  });

  useEffect(() => {
    if (completed) return;
    const timer = setInterval(() => {
      saveDraft(draftKey, { form }).then(() => setDraftSaved(true));
    }, 10000);
    return () => clearInterval(timer);
  }, [form, completed]);

  const manualSaveDraft = async () => {
    await saveDraft(draftKey, { form });
    setDraftSaved(true);
    toast.success("Draft saved locally");
  };

  const [editCertId, setEditCertId] = useState(null);

  useEffect(() => {
    // Check for edit data
    const editData = sessionStorage.getItem("edit_cert_data");
    if (editData) {
      try {
        const cert = JSON.parse(editData);
        sessionStorage.removeItem("edit_cert_data");
        setEditCertId(cert._edit_id);
        setForm((p) => ({ ...p, ...cert }));
        toast.info("Editing document — update and regenerate for free");
        return;
      } catch { /* ignore */ }
    }
    const repeatData = sessionStorage.getItem("repeat_cert_data");
    if (repeatData) {
      try {
        const prefill = JSON.parse(repeatData);
        sessionStorage.removeItem("repeat_cert_data");
        setForm((p) => ({ ...p, ...prefill }));
        toast.success("Customer and address details pre-filled from previous job");
        return;
      } catch { /* ignore */ }
    }
    loadDraft(draftKey).then((draft) => {
      if (draft?.form) { setForm((p) => ({ ...p, ...draft.form })); toast.info("Restored from saved draft"); }
    }).catch(() => {});
    authFetch(`${API}/api/user/contractor-details`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : {})
      .then((saved) => {
        if (saved?.contractor_trading_title) {
          setForm((p) => ({
            ...p,
            company_name: saved.contractor_trading_title || p.company_name,
            company_phone: saved.contractor_phone || p.company_phone,
            company_address: saved.contractor_address || p.company_address,
            gas_safe_no: saved.contractor_reg_no || p.gas_safe_no,
          }));
        }
      }).catch(() => {});
  }, []);

  const setF = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target?.value ?? e }));
  const toggle = (field) => setForm((p) => ({ ...p, [field]: !p[field] }));

  const buildPayload = () => ({
    ...form,
    signature,
    commissioning_date: form.commissioning_date ? format(form.commissioning_date, "dd/MM/yyyy") : "",
  });

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const res = await authFetch(`${API}/api/certificates/gas-safe/preview`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify(buildPayload()),
      });
      if (!res.ok) throw new Error("Preview failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setShowPreview(true);
    } catch (err) { toast.error(err.message); } finally { setPreviewing(false); }
  };

  const handleGenerate = async () => {
    if (!navigator.onLine) { await manualSaveDraft(); toast.error("You're offline — draft saved locally."); return; }
    setSubmitting(true);
    try {
      const isEdit = !!editCertId;
      const url = isEdit ? `${API}/api/certificates/${editCertId}` : `${API}/api/certificates/gas-safe`;
      const method = isEdit ? "PUT" : "POST";
      const res = await authFetch(url, {
        method, headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify(buildPayload()),
      });
      if (res.status === 402) { promptBuyCredits("gas"); return; }
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Failed");
      const data = await res.json();
      const certId = data.certificate_id || editCertId;
      setPdfUrl(`${API}/api/certificates/domestic-eic/${certId}/download`);
      setCompleted(true);
      toast.success(isEdit ? "Document updated!" : "Gas Safe document generated! 1 credit used.");
      deleteDraft(draftKey).catch(() => {});
    } catch (err) { toast.error(err.message); } finally { setSubmitting(false); }
  };

  const CheckItem = ({ field, label }) => (
    <label className="flex items-start gap-3 py-1.5 cursor-pointer group">
      <Checkbox checked={!!form[field]} onCheckedChange={() => toggle(field)}
        className="mt-0.5 w-5 h-5 border-2 rounded-none data-[state=checked]:bg-[#EF4444] data-[state=checked]:border-[#EF4444] flex-shrink-0"
        style={{ borderColor: 'var(--border-main)' }} />
      <span className="text-sm leading-snug" style={{ color: 'var(--text-primary)' }}>{label}</span>
    </label>
  );

  if (completed) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-page)' }}><AppHeader />
        <div className="max-w-3xl mx-auto px-4 py-12">
          <div className="form-container text-center" data-testid="gas-complete">
            <CheckCircle className="w-16 h-16 text-[#EF4444] mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Gas Safe Document Generated</h2>
            <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>Your Gas Boiler Commissioning Checklist has been created.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {pdfUrl && <a href={pdfUrl} target="_blank" rel="noopener noreferrer" data-testid="gas-download-pdf" className="brutalist-btn flex items-center justify-center gap-2"><FileDown className="w-4 h-4" /> Download PDF</a>}
              <a href="/dashboard" className="brutalist-btn-outline flex items-center justify-center gap-2">Back to Dashboard</a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-page)' }} data-testid="gas-safe-wizard">
      <AppHeader />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center gap-3 mb-6">
          <Flame className="w-6 h-6 text-[#EF4444]" />
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Gas Boiler Commissioning Checklist</h1>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-between mb-8" data-testid="gas-step-indicator">
          {STEPS.map((s, i) => (
            <div key={s.num} className="flex items-center flex-1">
              <div className="flex flex-col items-center">
                <div className={`w-10 h-10 flex items-center justify-center border-2 text-sm font-bold transition-all ${step > s.num ? "bg-[#EF4444] text-white border-[#EF4444]" : step === s.num ? "bg-[#EF4444] text-white border-[#EF4444]" : "bg-white text-[#B8B9BD] border-[#E4E4E7]"}`}>
                  {step > s.num ? <Check className="w-5 h-5" /> : s.num}
                </div>
                <span className={`text-xs mt-2 font-medium text-center hidden sm:block`} style={{ fontFamily: 'Outfit, sans-serif', color: step >= s.num ? 'var(--text-primary)' : '#B8B9BD' }}>{s.label}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-2 sm:mx-4 ${step > s.num ? "bg-[#EF4444]" : "bg-[#E4E4E7]"}`} />}
            </div>
          ))}
        </div>

        {/* STEP 1 */}
        {step === 1 && (
          <div className="form-container max-w-3xl mx-auto" data-testid="gas-step-1">
            <div className="border-b-2 pb-5 mb-5" style={{ borderColor: 'var(--border-light)' }}>
              <h3 className="text-sm font-bold text-[#EF4444] uppercase tracking-[0.15em] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Customer Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label>Customer name</Label><input className="brutalist-input" value={form.customer_name} onChange={setF("customer_name")} data-testid="gas-customer-name" /></div>
                <div><Label>Telephone</Label><input className="brutalist-input" value={form.customer_phone} onChange={setF("customer_phone")} /></div>
                <div className="md:col-span-2"><Label>Address</Label><input className="brutalist-input" value={form.customer_address} onChange={setF("customer_address")} /></div>
                <div><Label>Boiler make & model</Label><input className="brutalist-input" value={form.boiler_make_model} onChange={setF("boiler_make_model")} /></div>
                <div><Label>Boiler serial number</Label><input className="brutalist-input" value={form.boiler_serial} onChange={setF("boiler_serial")} /></div>
              </div>
            </div>

            <div className="border-b-2 pb-5 mb-5" style={{ borderColor: 'var(--border-light)' }}>
              <h3 className="text-sm font-bold text-[#EF4444] uppercase tracking-[0.15em] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Engineer & Company Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label>Commissioned by (name)</Label><input className="brutalist-input" value={form.engineer_name} onChange={setF("engineer_name")} /></div>
                <div><Label>Gas Safe register no</Label><input className="brutalist-input" value={form.gas_safe_no} onChange={setF("gas_safe_no")} /></div>
                <div><Label>Company name</Label><input className="brutalist-input" value={form.company_name} onChange={setF("company_name")} /></div>
                <div><Label>Company phone</Label><input className="brutalist-input" value={form.company_phone} onChange={setF("company_phone")} /></div>
                <div className="md:col-span-2"><Label>Company address</Label><input className="brutalist-input" value={form.company_address} onChange={setF("company_address")} /></div>
                <div>
                  <Label>Commissioning date</Label>
                  <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                    <PopoverTrigger asChild>
                      <button className="brutalist-input flex items-center justify-between cursor-pointer w-full" type="button">
                        <span className={form.commissioning_date ? "" : "text-[#9CA3AF]"}>{form.commissioning_date ? format(form.commissioning_date, "dd/MM/yyyy") : "Select date"}</span>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 border-2 border-[#0A0A0A] rounded-none" align="start">
                      <Calendar mode="single" selected={form.commissioning_date} onSelect={(d) => { setForm((p) => ({ ...p, commissioning_date: d })); setCalendarOpen(false); }} />
                    </PopoverContent>
                  </Popover>
                </div>
                <div><Label>Building Reg Notification No</Label><input className="brutalist-input" value={form.building_reg_number} onChange={setF("building_reg_number")} placeholder="If applicable" /></div>
              </div>
            </div>

            <div className="pb-2">
              <h3 className="text-sm font-bold text-[#EF4444] uppercase tracking-[0.15em] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Controls</h3>
              <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>Heating time & temperature control:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                <CheckItem field="ctrl_room_thermostat" label="Room thermostat and programmer/timer" />
                <CheckItem field="ctrl_prog_room_thermostat" label="Programmable room thermostat" />
                <CheckItem field="ctrl_load_weather_comp" label="Load/weather compensation" />
                <CheckItem field="ctrl_optimum_start" label="Optimum start control" />
              </div>
              <p className="text-xs mb-2 mt-3" style={{ color: 'var(--text-muted)' }}>Hot water time & temperature control:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                <CheckItem field="ctrl_cyl_thermostat" label="Cylinder thermostat and programmer/timer" />
                <CheckItem field="ctrl_combi_boiler" label="Combination boiler" />
              </div>
              <p className="text-xs mb-2 mt-3" style={{ color: 'var(--text-muted)' }}>Zone valves & other controls:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                <CheckItem field="ctrl_heating_zone_fitted" label="Heating zone valves — Fitted" />
                <CheckItem field="ctrl_heating_zone_not_req" label="Heating zone valves — Not required" />
                <CheckItem field="ctrl_hw_zone_fitted" label="Hot water zone valves — Fitted" />
                <CheckItem field="ctrl_hw_zone_not_req" label="Hot water zone valves — Not required" />
                <CheckItem field="ctrl_trv_fitted" label="Thermostatic radiator valves — Fitted" />
                <CheckItem field="ctrl_trv_not_req" label="TRVs — Not required" />
                <CheckItem field="ctrl_bypass_fitted" label="Automatic bypass — Fitted" />
                <CheckItem field="ctrl_bypass_not_req" label="Automatic bypass — Not required" />
                <CheckItem field="ctrl_boiler_interlock" label="Boiler interlock — Provided" />
              </div>
            </div>

            <div className="flex justify-between items-center mt-4">
              <button type="button" onClick={manualSaveDraft} className="brutalist-btn-outline !px-3 !py-1.5 text-xs flex items-center gap-1.5" data-testid="gas-save-draft-btn">
                <Save className="w-3.5 h-3.5" /> {draftSaved ? "Draft Saved" : "Save Draft"}
              </button>
              <button data-testid="gas-step-1-next" onClick={() => setStep(2)} className="brutalist-btn flex items-center gap-2">
                Readings <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <div className="form-container max-w-3xl mx-auto" data-testid="gas-step-2">
            <div className="border-b-2 pb-5 mb-5" style={{ borderColor: 'var(--border-light)' }}>
              <h3 className="text-sm font-bold text-[#EF4444] uppercase tracking-[0.15em] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>All Systems</h3>
              <div className="space-y-3">
                <YesNo label="System flushed & cleaned per BS7593?" value={form.system_flushed} onChange={setF("system_flushed")} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div><Label>System cleaner used</Label><input className="brutalist-input" value={form.system_cleaner} onChange={setF("system_cleaner")} /></div>
                  <div><Label>Inhibitor used</Label><input className="brutalist-input" value={form.system_inhibitor} onChange={setF("system_inhibitor")} /></div>
                </div>
                <YesNo label="Primary water system filter installed?" value={form.primary_filter} onChange={setF("primary_filter")} />
                {form.primary_filter === "yes" && (
                  <div className="w-48"><Label>Quantity (litres)</Label><input className="brutalist-input" value={form.filter_quantity} onChange={setF("filter_quantity")} /></div>
                )}
              </div>
            </div>

            <div className="border-b-2 pb-5 mb-5" style={{ borderColor: 'var(--border-light)' }}>
              <h3 className="text-sm font-bold text-[#EF4444] uppercase tracking-[0.15em] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Central Heating Mode</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div><Label>Gas rate</Label><input className="brutalist-input" value={form.ch_gas_rate} onChange={setF("ch_gas_rate")} /></div>
                <div><Label>Unit</Label>
                  <select className="brutalist-input" value={form.ch_gas_rate_unit} onChange={setF("ch_gas_rate_unit")}>
                    <option value="m³/hr">m³/hr</option><option value="ft³/hr">ft³/hr</option>
                  </select>
                </div>
                <div><Label>Burner pressure (mbar)</Label><input className="brutalist-input" value={form.ch_burner_pressure} onChange={setF("ch_burner_pressure")} /></div>
                <div><Label>Gas inlet pressure (mbar)</Label><input className="brutalist-input" value={form.ch_gas_inlet_pressure} onChange={setF("ch_gas_inlet_pressure")} /></div>
                <div><Label>Flow temp (°C)</Label><input className="brutalist-input" value={form.ch_flow_temp} onChange={setF("ch_flow_temp")} /></div>
                <div><Label>Return temp (°C)</Label><input className="brutalist-input" value={form.ch_return_temp} onChange={setF("ch_return_temp")} /></div>
              </div>
            </div>

            <div className="border-b-2 pb-5 mb-5" style={{ borderColor: 'var(--border-light)' }}>
              <h3 className="text-sm font-bold text-[#EF4444] uppercase tracking-[0.15em] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Combination Boilers Only</h3>
              <div className="space-y-3">
                <YesNo label="Hard water area (above 200ppm)?" value={form.is_hard_water} onChange={setF("is_hard_water")} />
                {form.is_hard_water === "yes" && <>
                  <YesNo label="Scale reducer fitted?" value={form.scale_reducer_fitted} onChange={setF("scale_reducer_fitted")} />
                  {form.scale_reducer_fitted === "yes" && (
                    <div className="w-64"><Label>Type of scale reducer</Label><input className="brutalist-input" value={form.scale_reducer_type} onChange={setF("scale_reducer_type")} /></div>
                  )}
                </>}
              </div>
            </div>

            <div className="border-b-2 pb-5 mb-5" style={{ borderColor: 'var(--border-light)' }}>
              <h3 className="text-sm font-bold text-[#EF4444] uppercase tracking-[0.15em] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Domestic Hot Water Mode</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div><Label>Gas rate</Label><input className="brutalist-input" value={form.dhw_gas_rate} onChange={setF("dhw_gas_rate")} /></div>
                <div><Label>Unit</Label>
                  <select className="brutalist-input" value={form.dhw_gas_rate_unit} onChange={setF("dhw_gas_rate_unit")}>
                    <option value="m³/hr">m³/hr</option><option value="ft³/hr">ft³/hr</option>
                  </select>
                </div>
                <div><Label>Burner pressure (mbar)</Label><input className="brutalist-input" value={form.dhw_burner_pressure} onChange={setF("dhw_burner_pressure")} /></div>
                <div><Label>Gas inlet pressure (mbar)</Label><input className="brutalist-input" value={form.dhw_gas_inlet_pressure} onChange={setF("dhw_gas_inlet_pressure")} /></div>
                <div><Label>Cold inlet temp (°C)</Label><input className="brutalist-input" value={form.dhw_cold_inlet_temp} onChange={setF("dhw_cold_inlet_temp")} /></div>
                <div><Label>Flow rate (l/min)</Label><input className="brutalist-input" value={form.dhw_flow_rate} onChange={setF("dhw_flow_rate")} /></div>
                <div><Label>Flow temp (°C)</Label><input className="brutalist-input" value={form.dhw_flow_temp} onChange={setF("dhw_flow_temp")} /></div>
              </div>
              <div className="mt-3">
                <YesNo label="Hot water checked at all outlets?" value={form.dhw_outlets_checked} onChange={setF("dhw_outlets_checked")} />
              </div>
            </div>

            <div className="border-b-2 pb-5 mb-5" style={{ borderColor: 'var(--border-light)' }}>
              <h3 className="text-sm font-bold text-[#EF4444] uppercase tracking-[0.15em] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Condensing Boilers Only</h3>
              <YesNo label="Condensate drain installed per manufacturer's instructions / BS5546 / BS6798?" value={form.condensate_installed} onChange={setF("condensate_installed")} />
            </div>

            <div className="border-b-2 pb-5 mb-5" style={{ borderColor: 'var(--border-light)' }}>
              <h3 className="text-sm font-bold text-[#EF4444] uppercase tracking-[0.15em] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>CO / CO₂ Readings</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div><Label>CO at max rate (ppm)</Label><input className="brutalist-input" value={form.co_max_ppm} onChange={setF("co_max_ppm")} /></div>
                <div><Label>CO/CO₂ ratio (max)</Label><input className="brutalist-input" value={form.co_max_ratio} onChange={setF("co_max_ratio")} /></div>
                <div><Label>CO at min rate (ppm)</Label><input className="brutalist-input" value={form.co_min_ppm} onChange={setF("co_min_ppm")} /></div>
                <div><Label>CO/CO₂ ratio (min)</Label><input className="brutalist-input" value={form.co_min_ratio} onChange={setF("co_min_ratio")} /></div>
              </div>
            </div>

            <div className="pb-2">
              <h3 className="text-sm font-bold text-[#EF4444] uppercase tracking-[0.15em] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Compliance Declarations</h3>
              <div className="space-y-3">
                <YesNo label="Heating & hot water system complies with Building Regulations?" value={form.complies_building_regs} onChange={setF("complies_building_regs")} />
                <YesNo label="Boiler installed & commissioned per manufacturer's instructions?" value={form.installed_per_instructions} onChange={setF("installed_per_instructions")} />
                <YesNo label="Operation demonstrated to and understood by the customer?" value={form.demonstrated_to_customer} onChange={setF("demonstrated_to_customer")} />
                <YesNo label="Manufacturer's literature left with customer?" value={form.literature_left} onChange={setF("literature_left")} />
              </div>
            </div>

            <div className="flex justify-between items-center mt-4">
              <button onClick={() => setStep(1)} className="brutalist-btn-outline flex items-center gap-2"><ArrowLeft className="w-4 h-4" /> Previous</button>
              <button onClick={() => setStep(3)} className="brutalist-btn flex items-center gap-2">Review <ArrowRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}

        {/* STEP 3 */}
        {step === 3 && (
          <div className="form-container max-w-3xl mx-auto" data-testid="gas-step-3">
            <h2 className="text-xl font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Review & Generate</h2>
            <p className="text-xs mb-6" style={{ color: 'var(--text-secondary)' }}>Review all details, then generate your Gas Boiler Commissioning Checklist.</p>

            {[
              ["Customer", [[form.customer_name, "Name"], [form.customer_address, "Address"], [form.boiler_make_model, "Boiler"]]],
              ["Engineer", [[form.engineer_name, "Name"], [form.gas_safe_no, "Gas Safe No"], [form.company_name, "Company"], [form.commissioning_date ? format(form.commissioning_date, "dd/MM/yyyy") : "", "Date"]]],
              ["CH Readings", [[`${form.ch_gas_rate} ${form.ch_gas_rate_unit}`, "Gas rate"], [`${form.ch_flow_temp}°C / ${form.ch_return_temp}°C`, "Flow / Return"]]],
              ["CO Readings", [[`${form.co_max_ppm} ppm / ${form.co_max_ratio}`, "Max rate"], [`${form.co_min_ppm} ppm / ${form.co_min_ratio}`, "Min rate"]]],
            ].map(([title, fields]) => (
              <div key={title} className="border-b-2 pb-4 mb-4" style={{ borderColor: 'var(--border-light)' }}>
                <h3 className="text-sm font-bold text-[#EF4444] uppercase tracking-[0.15em] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h3>
                {fields.map(([v, l]) => v && v.trim() ? (
                  <div key={l} className="flex justify-between py-1 text-sm">
                    <span style={{ color: 'var(--text-secondary)' }}>{l}</span>
                    <span className="font-semibold text-right max-w-[60%]">{v}</span>
                  </div>
                ) : null)}
              </div>
            ))}

            {/* Signature */}
            <div className="border-t-2 pt-5 mt-4" style={{ borderColor: 'var(--border-light)' }}>
              <SignaturePad label="Engineer's Signature" value={signature} onChange={setSignature} />
            </div>

            <div className="flex flex-col sm:flex-row justify-between gap-3 mt-4">
              <button onClick={() => setStep(2)} className="brutalist-btn-outline flex items-center gap-2"><ArrowLeft className="w-4 h-4" /> Previous</button>
              <div className="flex gap-3">
                <button onClick={handlePreview} disabled={previewing} className="brutalist-btn-outline flex items-center gap-2">
                  {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />} Preview PDF
                </button>
                <button data-testid="gas-generate-btn" onClick={handleGenerate} disabled={submitting} className="brutalist-btn flex items-center gap-2" style={{ backgroundColor: "#EF4444" }}>
                  {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</> : <><FileDown className="w-4 h-4" /> Generate Document</>}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <BuyCreditsModal open={showModal} onClose={closeModal} creditType={modalCreditType} onBuy={buyCredits} />
      <PdfPreviewModal open={showPreview} onClose={() => { setShowPreview(false); if (previewUrl) URL.revokeObjectURL(previewUrl); }} pdfUrl={previewUrl} loading={previewing} />
    </div>
  );
}
